/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import Debounce from 'lodash/debounce' // Npm: lodash library.
import _ from 'underscore' // Npm: utility module.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import SenderID from 'components/SenderID'
import VendorAccountUpsert from 'components/VendorAccountUpsert'
import VendorAccountDelete from 'components/VendorAccountDelete'
import AutoRateImport from 'components/AutoRateImport'
import VendorAccountDropDownOptions from 'components/VendorAccountDropDownOptions'
import { MemoizedInput } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import VendorAccountReadQuery from './__query__/index.vendorAccount.read.query'


/*
 * STYLES
 */
import { buttonStyle } from './index.style'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [vendorNameToSearch, setVendorNameToSearch] = React.useState('')
  const [vendorAccountNameToSearch, setVendorAccountNameToSearch] = React.useState('')
  const [rateNameToSearch, setRateNameToSearch] = React.useState('')
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({
    'items': [],
    // 'logicOperator': 'and',
    // 'quickFilterValues': [],
    // 'quickFilterLogicOperator': 'and'
  })
  const _setFilterDebounced = React.useMemo(() => _.debounce(setFilterModel, 500), [])

  const { 'onOpen': onVendorAccountCreateOpen, 'isOpen': isVendorAccountCreateOpen, 'onClose': onVendorAccountCreateClose } = useDisclosure()
  const { 'onOpen': onVendorAccountUpdateOpen, 'isOpen': isVendorAccountUpdateOpen, 'onClose': onVendorAccountUpdateClose } = useDisclosure()
  const { 'onOpen': onVendorAccountDeleteOpen, 'isOpen': isVendorAccountDeleteOpen, 'onClose': onVendorAccountDeleteClose } = useDisclosure()
  const { 'onOpen': onSenderIDAddOpen, 'isOpen': isSenderIDAddOpen, 'onClose': onSenderIDAddClose } = useDisclosure()
  const { 'onOpen': onAutoRateImportOpen, 'isOpen': isAutoRateImportOpen, 'onClose': onAutoRateImportClose } = useDisclosure()

  // Data assignment.
  const _isFilterApplied = filterModel?.items?.some(i => i.value != void 0 && i.value !== '')
  const _appliedFilter = _isFilterApplied ? filterModel : void 0

  const _QueryVendorAccountRead = useQuery(VendorAccountReadQuery, {
    'variables': {
      'vendorId': passOn?.vendorId,
      vendorNameToSearch,
      vendorAccountNameToSearch,
      rateNameToSearch,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize,
      'filter': _appliedFilter
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })
  const _onVendorNameToSearchInputChange = React.useRef(
    Debounce((e) => setVendorNameToSearch(e.target.value), 1000)
  ).current
  const _onVendorAccountNameToSearchInputChange = React.useRef(
    Debounce((e) => setVendorAccountNameToSearch(e.target.value), 1000)
  ).current
  const _onRateNameToSearchInputChange = React.useRef(
    Debounce((e) => setRateNameToSearch(e.target.value), 1000)
  ).current

  React.useEffect(() => {
    return () => {
      _onVendorNameToSearchInputChange.cancel?.()
      _onVendorAccountNameToSearchInputChange.cancel?.()
      _onRateNameToSearchInputChange.cancel?.()
    }
  }, [])
  const _tableHeaderHeightRef = React.useRef(0)
  const _isFirstLoadCompleted = React.useRef(false)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data assignment.
  if (!_QueryVendorAccountRead.loading && 0 < _QueryVendorAccountRead?.data?.VendorAccountRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryVendorAccountRead?.data?.VendorAccountRead?.length || !_.every(_.pluck(_QueryVendorAccountRead?.data?.VendorAccountRead, 'status'), j => _successFlags.includes(j))) _isFirstLoadCompleted.current = false

  // Columns definition.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'width': 80,
      'align': 'center',
      'headerAlign': 'center',
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'hide': true,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true
    },
    {
      'field': 'displayName',
      'headerName': 'Name',
      'flex': 1,
      'minWidth': 150,
      'align': 'center',
      'headerAlign': 'center',
      'editable': false,
      'sortable': true,
      'filterable': true
    },
    {
      'field': 'Vendor.displayName',
      'headerName': 'Vendor',
      'flex': 1,
      'minWidth': 150,
      'align': 'center',
      'headerAlign': 'center',
      'sortable': false,
      'filterable': true
    },
    {
      'field': 'Rate.displayName',
      'headerName': 'Rate',
      'flex': 1,
      'minWidth': 150,
      'align': 'center',
      'headerAlign': 'center',
      'sortable': false,
      'filterable': true
    },
    {
      'field': 'Vendor.Billing.currency',
      'headerName': 'Currency',
      'width': 120,
      'align': 'center',
      'headerAlign': 'center',
      'editable': false,
      'sortable': true,
      'filterable': false,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          {value ? (
            <Text
              borderRadius='10px'
              padding='5px 15px'
              lineHeight='18px'
              color='rgba(117, 81, 255, 1)'
              bg='rgba(117, 81, 255, .1)'
            >
              Active
            </Text>
          ) : (
            <Text
              borderRadius='10px'
              padding='5px 15px'
              lineHeight='18px'
              color='rgba(238, 93, 80, 1)'
              bg='rgba(255, 242, 242, 1)'
            >
              In Active
            </Text>
          )}
        </Box>
      )
    },

    {
      'field': 'createdAt',
      'headerName': 'CreatedAt',
      'width': 150,
      'align': 'center',
      'headerAlign': 'center',
      'type': 'date',
      'editable': false,
      'sortable': true,
      'filterable': false,
    },
    {
      'field': 'updatedAt',
      'headerName': 'UpdatedAt',
      'width': 150,
      'align': 'center',
      'headerAlign': 'center',
      'type': 'date',
      'editable': false,
      'sortable': true,
      'filterable': false,

    },
    {
      'field': 'actions',
      'headerName': 'Actions',
      'width': 100,
      'align': 'center',
      'headerAlign': 'center',
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => (
        <VendorAccountDropDownOptions
          vendorAccountId={row.id}
          smppId={row.smppId}
          onVendorAccountUpdate={onVendorAccountUpdateOpen}
          onVendorAccountDelete={onVendorAccountDeleteOpen}
          onAutoRateImport={onAutoRateImportOpen}
          onAddSenderID={onSenderIDAddOpen}
        />
      )
    }
  ], [
    onVendorAccountUpdateOpen,
    onVendorAccountDeleteOpen,
    onAutoRateImportOpen,
    onSenderIDAddOpen
  ])

  // Rows definition.
  const rows = React.useMemo(() =>
    _.compact(_QueryVendorAccountRead?.data?.VendorAccountRead?.map((item, index) =>
      _successFlags.includes(item.status) ? ({
        'id': item?.id || String.random(9),
        'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
        'displayName': item?.displayName ?? '-',
        'Vendor.displayName': item?.Vendor?.displayName ?? '-',
        'Rate.displayName': item?.Rate?.displayName ?? '-',
        'Vendor.Billing.currency': item?.Vendor?.Billing?.currency ?? '-',
        'status': item?.Smpp?.isActive || false,
        'createdAt': new Date(item?.createdAt),
        'updatedAt': new Date(item?.updatedAt),
        'smppId': item?.Smpp?.id
      }) : null
    )),
    [_QueryVendorAccountRead?.data?.VendorAccountRead, paginationModel.page, paginationModel.pageSize, _successFlags]
  )

  // Return component.
  return (
    <>
      <Flex className='vendorAccount base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          minH={_isCurrentViewMobile ? '115vh' : '98vh'}
          maxH={_isCurrentViewMobile ? '115vh' : '98vh'}
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between' flexWrap={{ 'base': 'wrap', 'md': 'nowrap' }}>
            <Tooltip label='Create new vendor account.' fontSize='sm'>
              <Button
                onClick={onVendorAccountCreateOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}>
                New Account
              </Button>
            </Tooltip>
            <DownloadToExcel
              cellsData={rows?.map((item, __index) => ({
                'S.No.': item.sno,
                'Name': item.displayName,
                'Vendor': item.vendorName,
                'Rate': item.rateName,
                'Currency': item.currency,
                'Status': item.status ? 'Active' : 'In Active',
                'CreatedAt': Moment(item.createdAt).format('YYYY-MM-DD'),
                'UpdatedAt': Moment(item.updatedAt).format('YYYY-MM-DD')
              }))}
              headersData={[
                'S.No.',
                'Name',
                'Vendor',
                'Rate',
                'Currency',
                'Status',
                'CreatedAt',
                'UpdatedAt'
              ].map(i => ({ 'key': i, 'label': i }))}
            />
          </Flex>

          {/* Filter */}
          <Flex
            gap='12px'
            flexWrap='wrap'
            bg='white'
            p='16px'
            borderRadius='12px'
            border='1px solid #C5CFE8'
            alignItems='flex-start'
          >
            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)' mb='8px'>
                Account Name
              </Text>
              <MemoizedInput
                placeholder='Search'
                className='filter searchInput'
                defaultValue={vendorAccountNameToSearch}
                onChange={_onVendorAccountNameToSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)' mb='8px'>
                Vendor Name
              </Text>
              <MemoizedInput
                placeholder='Search'
                className='filter searchInput'
                defaultValue={vendorNameToSearch}
                onChange={_onVendorNameToSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: '100%', md: '150px' }}
              w={{ base: '100%', md: '200px' }}
            >
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)' mb='8px'>
                Rate Name
              </Text>
              <MemoizedInput
                placeholder='Search'
                className='filter searchInput'
                defaultValue={rateNameToSearch}
                onChange={_onRateNameToSearchInputChange}
              />
            </Box>
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryVendorAccountRead?.data?.VendorAccountRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            filterMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            filterModel={filterModel}
            onFilterModelChange={_setFilterDebounced}
            pageSizeOptions={[8, 10, 20, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner
                isLoading={true}
                isEmpty={false}
                chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
              />,
              'noResultsOverlay': () => <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
              />,
              'noRowsOverlay': () => <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
              />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryVendorAccountRead?.loading && !_isFirstLoadCompleted.current}
          />

        </Flex>
        <Modal
          size='2xl'
          title='Vendor Account Create'
          isOpen={isVendorAccountCreateOpen}
          onClose={onVendorAccountCreateClose}
          isCentered={false}>
          <VendorAccountUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='2xl'
          title='Vendor Account Update'
          isOpen={isVendorAccountUpdateOpen}
          onClose={onVendorAccountUpdateClose}
          isCentered={false}>
          <VendorAccountUpsert />
        </Modal>
        <Modal
          size='md'
          title='Add Sender ID'
          isOpen={isSenderIDAddOpen}
          onClose={onSenderIDAddClose}>
          <SenderID />
        </Modal>
        <Modal
          size='md'
          title='Vendor Account Delete'
          isOpen={isVendorAccountDeleteOpen}
          onClose={onVendorAccountDeleteClose}>
          <VendorAccountDelete />
        </Modal>
        <Modal
          size='2xl'
          title='Auto Rate Import'
          isOpen={isAutoRateImportOpen}
          onClose={onAutoRateImportClose}>
          <AutoRateImport />
        </Modal>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
